import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import LevelAdd from "./LevelAdd";
import LevelEdit from "./LevelEdit";
import api from "../../config/URL";
import {
  ThemeProvider,
  createTheme,
  Menu,
  MenuItem,
  IconButton,
} from "@mui/material";
import GlobalDelete from "../../components/common/GlobalDelete";
import {
  MaterialReactTable,
  useMaterialReactTable,
} from "material-react-table";
import { MoreVert as MoreVertIcon } from "@mui/icons-material";
import { exportVisibleColumnsToCSV } from "../../components/common/ExportCsv";
import {
  getColumnPreference,
  saveColumnPreference,
} from "../../utility/mrtColumnPersistence";

const MODULE_KEY = "LEVEL";

const Level = () => {
  const [filters, setFilters] = useState({
    level: "",
    code: "",
  });

  const navigate = useNavigate();
  /* -------------------- PERMISSIONS -------------------- */
  const storedScreens = JSON.parse(localStorage.getItem("screens") || "{}");
  const canIndex = Boolean(storedScreens?.levelIndex);
  const canRead = Boolean(storedScreens?.levelRead);
  const canCreate = Boolean(storedScreens?.levelCreate);
  const canUpdate = Boolean(storedScreens?.levelUpdate);
  const canDelete = Boolean(storedScreens?.levelDelete);
  const showActionColumn = canUpdate || canDelete;

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [selectedId, setSelectedId] = useState(null);

  const defaultColumnVisibility = {
    createdBy: false,
    createdAt: false,
    updatedBy: false,
    updatedAt: false,
  };

  const [columnVisibility, setColumnVisibility] = useState(() =>
    getColumnPreference(MODULE_KEY, defaultColumnVisibility),
  );

  /* -------------------- COLUMNS -------------------- */
  const columns = useMemo(() => {
    const baseColumns = [
      {
        accessorFn: (row, index) => index + 1,
        id: "sno",
        header: "S.NO",
        size: 40,
        Cell: ({ cell }) => (
          <span style={{ textAlign: "center" }}>{cell.getValue()}</span>
        ),
      },
      {
        accessorKey: "status",
        enableHiding: true,
        header: "Status",
        Cell: ({ row }) =>
          row.original.status === "ACTIVE" ||
          row.original.status === "active" ||
          row.original.status === "Active" ? (
            <span
              className="badge badges-Green fw-light"
              // style={{ backgroundColor: "#287f71" }}
            >
              Active
            </span>
          ) : row.original.status === "In Active" ||
            row.original.status === "Inactive" ||
            row.original.status === "string" ? (
            <span
              className="badge badges-orange fw-light"
              // style={{ backgroundColor: "#eb862a" }}
            >
              In Active
            </span>
          ) : null,
      },
      { accessorKey: "level", header: "Level" },
      { accessorKey: "levelCode", header: "Level Code" },
      { accessorKey: "subjectName", header: "Subject Name" },
      { accessorKey: "createdBy", header: "Created By" },
      {
        accessorKey: "createdAt",
        header: "Created On",
        Cell: ({ cell }) => cell.getValue()?.substring(0, 10),
      },
      {
        accessorKey: "updatedAt",
        header: "Updated On",
        Cell: ({ cell }) => cell.getValue()?.substring(0, 10) || "",
      },
      {
        accessorKey: "updatedBy",
        header: "Updated By",
        Cell: ({ cell }) => cell.getValue() || "",
      },
    ];

    if (!showActionColumn) return baseColumns;

    const actionColumn = {
      id: "actions",
      header: "",
      size: 20,
      enableSorting: false,
      enableColumnActions: true,
      Cell: ({ row }) => (
        <IconButton
          onClick={(e) => {
            e.stopPropagation();
            setMenuAnchor(e.currentTarget);
            setSelectedId(row.original.id);
          }}
        >
          <MoreVertIcon />
        </IconButton>
      ),
    };

    return [
      baseColumns[0], // S.NO
      actionColumn, // Actions
      ...baseColumns.slice(1),
    ];
  }, [showActionColumn]);

  /* -------------------- FETCH DATA -------------------- */
  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await api.get("/getAllCourseLevels");
      setData(response.data);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    if (canIndex) fetchData();
  }, [canIndex]);

  /* -------------------- FILTER DATA -------------------- */
  const filteredData = useMemo(() => {
    return data.filter((item) => {
      const matchesLevel = item.level
        ?.toLowerCase()
        .includes(filters.level.toLowerCase());
      const matchesCode = item.levelCode
        ?.toLowerCase()
        .includes(filters.code.toLowerCase());
      return matchesLevel && matchesCode;
    });
  }, [data, filters]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const clearFilter = () => {
    setFilters({ level: "", code: "" });
  };

  const handleMenuClose = () => setMenuAnchor(null);

  /* -------------------- TABLE INSTANCE -------------------- */
  const table = useMaterialReactTable({
    columns,
    data: filteredData,
    state: {
      columnVisibility,
    },
    onColumnVisibilityChange: (updater) => {
      const newVisibility =
        typeof updater === "function" ? updater(columnVisibility) : updater;

      setColumnVisibility(newVisibility);
      saveColumnPreference(MODULE_KEY, newVisibility);
    },
    enableColumnActions: false,
    enableColumnFilters: false,
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    muiTableBodyRowProps: ({ row }) => ({
      onClick: canRead
        ? () => navigate(`/level/view/${row.original.id}`)
        : undefined,
      style: { cursor: canRead ? "pointer" : "default" },
    }),
    initialState: {
      pagination: { pageSize: 50, pageIndex: 0 },
    },
    renderTopToolbarCustomActions: ({ table }) => (
      <button
        className="btn btn-sm btn-border"
        onClick={() =>
          exportVisibleColumnsToCSV(table, filteredData, "LevelList.csv")
        }
      >
        Export CSV
      </button>
    ),
  });

  if (!canIndex) return null;

  /* -------------------- THEME -------------------- */
  const theme = createTheme({
    components: {
      MuiTableCell: {
        styleOverrides: {
          head: {
            color: "#535454 !important",
            backgroundColor: "#e6edf7 !important",
            fontWeight: "400 !important",
            fontSize: "13px !important",
            textAlign: "center !important",
          },
        },
      },
      MuiSwitch: {
        styleOverrides: {
          root: {
            "&.Mui-disabled .MuiSwitch-track": {
              backgroundColor: "#f5e1d0",
              opacity: 1,
            },
            "&.Mui-disabled .MuiSwitch-thumb": {
              color: "#eb862a",
            },
          },
          track: { backgroundColor: "#e0e0e0" },
          thumb: { color: "#eb862a" },
          switchBase: {
            "&.Mui-checked": { color: "#eb862a" },
            "&.Mui-checked + .MuiSwitch-track": {
              backgroundColor: "#eb862a",
            },
          },
        },
      },
    },
  });

  return (
    <div className="container-fluid my-4 center">
      {/* -------------------- BREADCRUMB -------------------- */}
      <ol
        className="breadcrumb my-3 px-1"
        style={{ listStyle: "none", padding: 0, margin: 0 }}
      >
        <li>
          <Link to="/" className="custom-breadcrumb">
            Home
          </Link>
          <span className="breadcrumb-separator "> &gt; </span>
        </li>
        <li>
          Course Management
          <span className="breadcrumb-separator"> &gt; </span>
        </li>
        <li className="breadcrumb-item active" aria-current="page">
          Level
        </li>
      </ol>

      <div className="card">
        <div
          className="mb-3 d-flex justify-content-between align-items-center p-1"
          style={{ background: "#f5f7f9" }}
        >
          <div className="d-flex align-items-center">
            <div className="d-flex">
              <div className="dot active"></div>
            </div>
            <span className="me-2 text-muted">
              This database shows the list of{" "}
              <span className="bold" style={{ color: "#287f71" }}>
                Level
              </span>
            </span>
          </div>
        </div>

        {/* -------------------- FILTERS -------------------- */}
        <div className="mb-3 d-flex justify-content-between">
          <div className="individual_fliters d-lg-flex ">
            <div className="form-group mb-0 ms-2 mb-1">
              <input
                type="text"
                className="form-control form-control-sm center_list"
                style={{ width: "160px" }}
                placeholder="Level"
                name="level"
                value={filters.level}
                onChange={handleFilterChange}
              />
            </div>
            <div className="form-group mb-0 ms-2 mb-1">
              <input
                type="text"
                className="form-control form-control-sm center_list"
                style={{ width: "160px" }}
                placeholder="Code"
                name="code"
                value={filters.code}
                onChange={handleFilterChange}
              />
            </div>
            <div className="form-group mb-0 ms-2 mb-1 ">
              <button
                type="button"
                className="btn btn-sm btn-border"
                onClick={clearFilter}
              >
                Clear
              </button>
            </div>
          </div>
          {canCreate && (
            <div className="d-flex justify-content-end align-items-center me-2">
              <span>
                <LevelAdd onSuccess={fetchData} />
              </span>
            </div>
          )}
        </div>
        {/* -------------------- TABLE -------------------- */}
        {loading ? (
          <div className="loader-container">
            <div className="loading">
              <span></span>
              <span></span>
              <span></span>
              <span></span>
              <span></span>
              <span></span>
            </div>
          </div>
        ) : (
          <>
            <ThemeProvider theme={theme}>
              <MaterialReactTable table={table} />
            </ThemeProvider>

            <Menu
              id="action-menu"
              anchorEl={menuAnchor}
              open={Boolean(menuAnchor)}
              onClose={handleMenuClose}
            >
              {/* <MenuItem onClick={() => navigate(`/level/view/${selectedId}`)}>
                View
              </MenuItem> */}
              {canUpdate && (
                <MenuItem>
                  <LevelEdit
                    onSuccess={fetchData}
                    id={selectedId}
                    handleMenuClose={handleMenuClose}
                  />
                </MenuItem>
              )}
              {canDelete && (
                <MenuItem>
                  <GlobalDelete
                    path={`/deleteCourseLevel/${selectedId}`}
                    onDeleteSuccess={fetchData}
                    onOpen={handleMenuClose}
                  />
                </MenuItem>
              )}
            </Menu>
          </>
        )}
      </div>
    </div>
  );
};

export default Level;
